/* src/utils/kbContent.js
 * Pure helpers to parse and clean KB content for display (no React, no writes).
 */

import { GUIDED_SECTION_LABELS } from './kbAssemble';

const KNOWN_LABELS = [
  'Site Name:',
  'Brand Color:',
  'Title:',
  'Description:',
  'Main Headings (H1):',
  'Section Headings (H2):',
  'Subsection Headings (H3):',
  'Content:',
  'Full text excerpt:',
];

const HEADING_LABELS = [
  'Main Headings (H1):',
  'Section Headings (H2):',
  'Subsection Headings (H3):',
];

const SOURCE_REGEX = /\[From:\s*(https?:\/\/[^\]\s]+)\]/gi;
const HEX_REGEX = /#([0-9a-f]{3}|[0-9a-f]{6})\b/gi;

const EMPTY_PARSE = {
  sources: [],
  siteName: '',
  title: '',
  description: '',
  headings: [],
  body: '',
  isStructured: false,
  isGuided: false,
  sections: [],
};

const NAMED_ENTITIES = {
  nbsp: ' ',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  rsquo: '\u2019',
  lsquo: '\u2018',
  rdquo: '\u201D',
  ldquo: '\u201C',
  ndash: '\u2013',
  mdash: '\u2014',
  hellip: '\u2026',
  copy: '\u00A9',
  reg: '\u00AE',
  trade: '\u2122',
};

export function decodeHtmlEntities(text) {
  let current = text || '';
  for (let pass = 0; pass < 3; pass += 1) {
    const next = current.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, entity) => {
      if (entity[0] === '#') {
        const code =
          entity[1] === 'x' || entity[1] === 'X'
            ? parseInt(entity.slice(2), 16)
            : parseInt(entity.slice(1), 10);
        if (!Number.isFinite(code) || code < 0) return match;
        try {
          return String.fromCodePoint(code);
        } catch {
          return match;
        }
      }
      return NAMED_ENTITIES[entity.toLowerCase()] ?? match;
    });
    if (next === current) return next;
    current = next;
  }
  return current;
}

function collapseWhitespace(text) {
  if (!text) return '';
  return text.replace(/\s+/g, ' ').trim();
}

function dedupeHeadings(items, cap = 12) {
  const seen = new Set();
  const result = [];
  for (const item of items) {
    const trimmed = item.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
    if (result.length >= cap) break;
  }
  return result;
}

function splitHeadingValue(value) {
  return value.split(',').map((part) => part.trim()).filter(Boolean);
}

function stripNoise(text) {
  return text
    .replace(SOURCE_REGEX, '')
    .replace(/\n\n---\n\n/g, '\n\n')
    .replace(/\n---\n/g, '\n')
    .trim();
}

function extractSources(text) {
  const sources = [];
  let match = SOURCE_REGEX.exec(text);
  while (match) {
    sources.push(match[1].trim());
    match = SOURCE_REGEX.exec(text);
  }
  SOURCE_REGEX.lastIndex = 0;
  return sources;
}

function mergeSources(...lists) {
  const seen = new Set();
  const result = [];
  for (const list of lists) {
    for (const url of list) {
      const trimmed = (url || '').trim();
      if (!trimmed || seen.has(trimmed)) continue;
      seen.add(trimmed);
      result.push(trimmed);
    }
  }
  return result;
}

function parseSourcesLine(text) {
  if (!text) return [];
  return text.split(',').map((part) => part.trim()).filter((part) => /^https?:\/\//i.test(part));
}

function matchLabelSegment(segment) {
  for (const label of KNOWN_LABELS) {
    if (segment.startsWith(label)) {
      return { label, value: segment.slice(label.length).trim() };
    }
  }
  return null;
}

function cleanBodyText(text) {
  if (!text) return '';
  const decoded = decodeHtmlEntities(text);
  const paragraphs = decoded.split(/\n\n+/).map((p) => collapseWhitespace(p)).filter(Boolean);
  if (paragraphs.length <= 1) return collapseWhitespace(decoded);
  return paragraphs.join('\n\n');
}

function preserveGuidedBody(text) {
  if (!text) return '';
  return decodeHtmlEntities(text).trim();
}

function trimToLength(text, maxLen) {
  const trimmed = text.trim();
  if (trimmed.length <= maxLen) return trimmed;

  const slice = trimmed.slice(0, maxLen);
  const lastSpace = slice.lastIndexOf(' ');
  const lastPeriod = Math.max(slice.lastIndexOf('. '), slice.lastIndexOf('! '), slice.lastIndexOf('? '));
  const cutAt = lastPeriod > maxLen * 0.5 ? lastPeriod + 1 : (lastSpace > 0 ? lastSpace : maxLen);
  return `${trimmed.slice(0, cutAt).trimEnd()}…`;
}

function stripSummaryNoise(text) {
  return collapseWhitespace(
    text
      .replace(SOURCE_REGEX, '')
      .replace(/Brand Color:\s*#[0-9a-f]{3,6}/gi, '')
      .replace(/Site Name:\s*/gi, '')
      .replace(/Title:\s*/gi, '')
      .replace(/Description:\s*/gi, '')
      .replace(/Main Headings \(H1\):\s*/gi, '')
      .replace(/Section Headings \(H2\):\s*/gi, '')
      .replace(/Subsection Headings \(H3\):\s*/gi, '')
      .replace(/Content:\s*/gi, '')
      .replace(/Full text excerpt:\s*/gi, '')
      .replace(HEX_REGEX, '')
      .replace(/\[From:/gi, '')
  );
}

function findGuidedLabelPositions(cleaned) {
  const positions = [];
  for (const label of GUIDED_SECTION_LABELS) {
    let searchFrom = 0;
    while (searchFrom < cleaned.length) {
      const idx = cleaned.indexOf(label, searchFrom);
      if (idx === -1) break;
      const atBoundary = idx === 0 || cleaned[idx - 1] === '\n';
      if (atBoundary) {
        positions.push({ label, idx, end: idx + label.length });
        break;
      }
      searchFrom = idx + 1;
    }
  }
  return positions.sort((a, b) => a.idx - b.idx);
}

function parseGuidedContent(cleaned, fromSources = []) {
  const positions = findGuidedLabelPositions(cleaned);
  if (positions.length === 0) return null;

  const sections = [];
  let guidedSources = [];

  for (let i = 0; i < positions.length; i += 1) {
    const { label, end } = positions[i];
    const nextIdx = i + 1 < positions.length ? positions[i + 1].idx : cleaned.length;
    const rawBody = cleaned.slice(end, nextIdx).trim();
    if (!rawBody && label !== 'Sources:') continue;

    if (label === 'Sources:') {
      guidedSources = parseSourcesLine(rawBody);
      sections.push({ label, body: rawBody });
      continue;
    }

    const body = label === 'FAQ:' || label === 'Reference material:' || label === 'What we offer:'
      ? preserveGuidedBody(rawBody)
      : cleanBodyText(rawBody);

    if (body) sections.push({ label, body });
  }

  const overviewSection = sections.find((s) => s.label === 'Overview:');
  const body = overviewSection?.body || sections[0]?.body || '';

  return {
    sources: mergeSources(fromSources, guidedSources),
    siteName: '',
    title: '',
    description: '',
    headings: [],
    body,
    isStructured: true,
    isGuided: true,
    sections,
  };
}

export function parseKbContent(raw) {
  if (!raw || typeof raw !== 'string') {
    return { ...EMPTY_PARSE };
  }

  const fromSources = extractSources(raw);
  const cleaned = stripNoise(raw);
  if (!cleaned) {
    return { ...EMPTY_PARSE, sources: fromSources };
  }

  const segments = cleaned.split(/\n\n+/).filter(Boolean);
  const labelValues = {};
  let foundScrapeLabel = false;

  for (const segment of segments) {
    const matched = matchLabelSegment(segment);
    if (!matched) continue;
    foundScrapeLabel = true;
    if (matched.label === 'Brand Color:') continue;
    if (!labelValues[matched.label]) {
      labelValues[matched.label] = matched.value;
    }
  }

  if (foundScrapeLabel) {
    const headingParts = [];
    for (const label of HEADING_LABELS) {
      if (labelValues[label]) {
        headingParts.push(...splitHeadingValue(labelValues[label]));
      }
    }

    const bodyRaw = labelValues['Content:'] || labelValues['Full text excerpt:'] || '';

    return {
      sources: fromSources,
      siteName: collapseWhitespace(decodeHtmlEntities(labelValues['Site Name:'] || '')),
      title: collapseWhitespace(decodeHtmlEntities(labelValues['Title:'] || '')),
      description: collapseWhitespace(decodeHtmlEntities(labelValues['Description:'] || '')),
      headings: dedupeHeadings(headingParts.map((h) => decodeHtmlEntities(h))),
      body: cleanBodyText(bodyRaw),
      isStructured: false,
      isGuided: false,
      sections: [],
    };
  }

  const guided = parseGuidedContent(cleaned, fromSources);
  if (guided) return guided;

  return {
    sources: fromSources,
    siteName: '',
    title: '',
    description: '',
    headings: [],
    body: cleanBodyText(cleaned),
    isStructured: true,
    isGuided: false,
    sections: [],
  };
}

export function kbSummary(raw, maxLen = 160) {
  const parsed = parseKbContent(raw);

  let candidate = '';
  if (parsed.isGuided) {
    const overviewSection = parsed.sections.find((s) => s.label === 'Overview:');
    candidate = overviewSection?.body || parsed.body;
  } else {
    candidate = parsed.description
      || parsed.title
      || parsed.body
      || parsed.siteName;
  }

  candidate = stripSummaryNoise(candidate);
  if (!candidate) return 'No content yet';
  return trimToLength(candidate, maxLen);
}

export function splitKbBodyParagraphs(body) {
  if (!body || typeof body !== 'string') return [];

  const byBlankLine = body.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  if (byBlankLine.length > 1) return byBlankLine;

  const text = collapseWhitespace(body);
  if (text.length <= 400) return text ? [text] : [];

  const sentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [text];
  const paragraphs = [];
  let current = '';

  for (const sentence of sentences) {
    const next = current ? `${current} ${sentence.trim()}` : sentence.trim();
    const sentenceCount = (next.match(/[.!?](?:\s|$)/g) || []).length;
    if (sentenceCount >= 3 && current) {
      paragraphs.push(current.trim());
      current = sentence.trim();
    } else {
      current = next;
    }
  }

  if (current.trim()) paragraphs.push(current.trim());
  return paragraphs.length ? paragraphs : [text];
}

export function composeOverview(parsed) {
  if (parsed.isGuided) {
    const overviewSection = parsed.sections.find((s) => s.label === 'Overview:');
    return overviewSection?.body || parsed.body;
  }
  if (parsed.isStructured) return parsed.body;
  const parts = [parsed.siteName, parsed.title, parsed.description].filter(Boolean);
  return parts.join('. ').replace(/\.\./g, '.').trim();
}

export function getParsedChatOverview(chatContent) {
  const parsed = parseKbContent(chatContent);
  if (parsed.isGuided) {
    const overviewSection = parsed.sections.find((s) => s.label === 'Overview:');
    return overviewSection?.body || parsed.body || chatContent.trim();
  }
  if (parsed.isStructured) return parsed.body;
  const parts = [parsed.description, parsed.title, parsed.body].filter(Boolean);
  return parts[0] || parsed.body || chatContent.trim();
}
